import { AlertTriangle, RefreshCw, WifiOff } from 'lucide-react';
import { Alert, AlertDescription, Skeleton } from '@/lib/macondo';
import { Btn } from '@/components/Btn';
import { useI18n } from '@/i18n/I18nProvider';
import { ApiClientError } from '@/api/errors';
import type { MessageKey } from '@/i18n/translate';

/** Message key and variables for an API failure (plan 5.4 error.* keys). */
export function errorMessage(error: unknown): { key: MessageKey; vars?: Record<string, number> } {
  if (error instanceof ApiClientError) {
    if (error.code === 'network') return { key: 'error.network' };
    if (error.code === 'not_found') return { key: 'error.notFound' };
    if (error.code === 'rate_limited' || error.code === 'too_many_attempts' || error.status === 429) {
      return error.retryAfterSeconds ? { key: 'error.rateLimited', vars: { seconds: error.retryAfterSeconds } } : { key: 'error.generic' };
    }
  }
  return { key: 'error.generic' };
}

/** Inline failure with a retry button. `role=alert` so it is announced when it appears. */
export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: (() => void) | undefined; className?: string | undefined }) {
  const { t } = useI18n();
  const { key, vars } = errorMessage(error);
  const offline = error instanceof ApiClientError && error.code === 'network';
  const retryable = !(error instanceof ApiClientError && error.code === 'not_found');
  return (
    <Alert tone="critical" role="alert" className={className}>
      <div className="flex items-start gap-300">
        {offline ? <WifiOff size={20} aria-hidden="true" className="mt-050 shrink-0" /> : <AlertTriangle size={20} aria-hidden="true" className="mt-050 shrink-0" />}
        <div className="flex flex-col items-start gap-300">
          <AlertDescription>{t(key, vars)}</AlertDescription>
          {onRetry && retryable && (
            <Btn type="button" variant="secondary" size="sm" onClick={onRetry} className="min-h-[44px]">
              <RefreshCw size={16} aria-hidden="true" />
              {t('common.retry')}
            </Btn>
          )}
        </div>
      </div>
    </Alert>
  );
}

/** Skeleton block. The wrapper is a polite status region so assistive tech hears "Loading". */
export function LoadingState({ rows = 3, className }: { rows?: number; className?: string }) {
  const { t } = useI18n();
  return (
    <div role="status" aria-live="polite" aria-busy="true" className={className}>
      <span className="sr-only">{t('common.loading')}</span>
      <div className="flex flex-col gap-300" aria-hidden="true">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className="h-1200 w-full" />
        ))}
      </div>
    </div>
  );
}

export function EmptyState({ children }: { children: string }) {
  return (
    <div className="bg-surface-primary border-border-primary text-typography-secondary text-body rounded-card-sm border border-dashed p-600 text-center">
      {children}
    </div>
  );
}
